/**
 * Reading and writing the §3.1 preference keys as React state.
 *
 * `KeyValuePort` is a pull interface with a cross-document change event; React
 * needs a push subscription per key. This module is the adapter between them,
 * and it exists once rather than per feature because of the failure mode it
 * closes: the storage event never fires in the tab that made the change, so a
 * component that only subscribed to the port would show a stale value in the
 * very tab where the user just changed the setting.
 */

import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useMemo, useSyncExternalStore } from "react";
import type { Cancel, KeyValuePort } from "@ff/domain/ports";
import type { PreferenceDefinition } from "@ff/store";
import { usePreferenceStore } from "./ports";

/**
 * How one key's string becomes a value and back.
 *
 * `decode` takes `string | undefined` rather than `string` because "never
 * written" is a distinct case from "written empty", and every preference in
 * §3.1 has a documented default that only the absent case may fall back to.
 *
 * Codecs must be module-level constants: `usePreference` memoises the decoded
 * value on the codec's identity, and a codec rebuilt each render would decode
 * on every render for no benefit.
 */
export interface PreferenceCodec<T> {
  decode(raw: string | undefined): T;
  encode(value: T): string;
}

/**
 * Absent means the default. Anything that is not the literal `"true"`/`"false"`
 * pair is also treated as absent: a key corrupted by an unrelated writer must
 * resolve to the documented default rather than to `Boolean("0")`.
 */
export function booleanPreference(fallback: boolean): PreferenceCodec<boolean> {
  return {
    decode(raw) {
      if (raw === "true") return true;
      if (raw === "false") return false;
      return fallback;
    },
    encode(value) {
      return value ? "true" : "false";
    },
  };
}

/** A closed set of string values; anything outside it resolves to `fallback`. */
export function stringUnionPreference<T extends string>(
  values: readonly T[],
  fallback: T,
): PreferenceCodec<T> {
  return {
    decode(raw) {
      if (raw === undefined) return fallback;
      return values.includes(raw as T) ? (raw as T) : fallback;
    },
    encode(value) {
      return value;
    },
  };
}

/**
 * Fans one port subscription out to per-key React subscribers, and re-notifies
 * the local tab on its own writes.
 *
 * The port subscription is attached on the first key subscriber and detached
 * with the last, rather than in the constructor. That is what makes the
 * observer survive React's development-mode mount/unmount/remount: a
 * constructor subscription would be cancelled by the first teardown and never
 * re-established, leaving cross-tab preference changes silently unobserved.
 */
class PreferenceObserver {
  readonly #port: KeyValuePort;
  readonly #listeners = new Map<string, Set<() => void>>();
  #cancel: Cancel | null = null;

  constructor(port: KeyValuePort) {
    this.#port = port;
  }

  read(key: string): string | undefined {
    return this.#port.read(key);
  }

  /**
   * Returns the port's own answer. A refused write (a full quota) is not fatal
   * for anything stored here, but the caller is still told, and the listeners
   * still fire so the UI re-reads and shows what actually survived.
   */
  write(key: string, value: string): boolean {
    const stored = this.#port.write(key, value);
    this.#notify(key);
    return stored;
  }

  remove(key: string): void {
    this.#port.remove(key);
    this.#notify(key);
  }

  subscribe(key: string, listener: () => void): Cancel {
    const existing = this.#listeners.get(key);
    const set = existing ?? new Set<() => void>();
    if (existing === undefined) this.#listeners.set(key, set);
    set.add(listener);
    this.#attach();
    return () => {
      set.delete(listener);
      if (set.size === 0) this.#listeners.delete(key);
      if (this.#listeners.size === 0) this.#detach();
    };
  }

  #attach(): void {
    if (this.#cancel !== null) return;
    this.#cancel = this.#port.subscribe((key) => {
      this.#notify(key);
    });
  }

  #detach(): void {
    this.#cancel?.();
    this.#cancel = null;
  }

  #notify(key: string): void {
    const set = this.#listeners.get(key);
    if (set === undefined) return;
    for (const listener of set) listener();
  }
}

const PreferenceObserverContext = createContext<PreferenceObserver | null>(null);

function usePreferenceObserver(): PreferenceObserver {
  const observer = useContext(PreferenceObserverContext);
  if (observer === null) {
    throw new Error(
      "usePreference() requires an enclosing <PreferenceProvider>. It is placed by " +
        "<AppStateProvider>; a test rendering a bare surface needs one too.",
    );
  }
  return observer;
}

export interface PreferenceProviderProps {
  readonly children?: ReactNode;
}

export function PreferenceProvider({ children }: PreferenceProviderProps): ReactNode {
  const port = usePreferenceStore();
  const observer = useMemo(() => new PreferenceObserver(port), [port]);

  return (
    <PreferenceObserverContext.Provider value={observer}>
      {children}
    </PreferenceObserverContext.Provider>
  );
}

export type PreferenceHandle<T> = readonly [T, (next: T) => void];

/**
 * One preference key as state. The setter writes through immediately: nothing
 * in §3.1 is worth a debounce, and a deferred write is a preference lost to a
 * tab close.
 */
export function usePreference<T>(key: string, codec: PreferenceCodec<T>): PreferenceHandle<T> {
  const observer = usePreferenceObserver();

  const subscribe = useCallback(
    (onChange: () => void) => observer.subscribe(key, onChange),
    [observer, key],
  );
  // The snapshot is the raw string — a primitive — so `useSyncExternalStore`'s
  // identity check holds without a cache. Decoding happens outside the store,
  // where a structural result would otherwise be a new object every render.
  const getSnapshot = useCallback(() => observer.read(key), [observer, key]);
  const raw = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const value = useMemo(() => codec.decode(raw), [codec, raw]);
  const set = useCallback(
    (next: T) => {
      observer.write(key, codec.encode(next));
    },
    [observer, key, codec],
  );

  return [value, set];
}

/**
 * The same binding for a preference whose key and default are already declared
 * as a {@link PreferenceDefinition} in `@ff/store`.
 *
 * The §3.1 table lives there (P16 owns it), and several of its entries take
 * their key and default from `@ff/domain` in turn. A surface that called
 * `usePreference("onboarding.completed.v1", booleanPreference(false))` would be
 * restating both facts a third time, in a file that has no way to notice when
 * the table changes. Passing the definition means the key and the default reach
 * React from the one place that states them.
 *
 * A `PreferenceDefinition<T>` already satisfies `PreferenceCodec<T>`
 * structurally, so this is a projection rather than an adapter — nothing is
 * translated between the two vocabularies, which is exactly why they can be
 * allowed to remain two types.
 */
export function usePreferenceDefinition<T>(
  definition: PreferenceDefinition<T>,
): PreferenceHandle<T> {
  return usePreference(definition.key, definition);
}
